import * as React from 'react';
import { Check, ChevronsUpDown, Search, Briefcase, X, Star, Clock, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ProjectDetails } from '@/types';
import { useProjectSelector } from '@/hooks/use-project-selector';
import { useScheduler } from '@/contexts/SchedulerContext';
import { PROJECT_STATUSES, normalizeStatusValue } from '@/lib/statuses';
import { ProjectSearchModal } from './ProjectSearchModal';

interface EnhancedProjectSelectProps {
  projects: ProjectDetails[];
  selectedProjectId?: string;
  onSelect: (projectId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function EnhancedProjectSelect({
  projects,
  selectedProjectId,
  onSelect,
  disabled = false,
  placeholder = 'Select project...',
  className,
}: EnhancedProjectSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [availableHeight, setAvailableHeight] = React.useState<number>(300); // Default fallback height
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const { settings } = useScheduler();

  const configuredProjectStatuses = React.useMemo(() => {
    const raw = settings.availableProjectStatuses === 'all'
      ? PROJECT_STATUSES.map(s => s.value)
      : settings.availableProjectStatuses;
    // Normalize configured values (e.g. 'active' → 'in progress') so they
    // match the canonical PROJECT_STATUSES values used for filtering.
    return [...new Set(raw.map(s => normalizeStatusValue(s)))];
  }, [settings.availableProjectStatuses]);

  const {
    enrichedProjects,
    favouriteProjects,
    recentProjects,
    toggleFavourite,
    recordProjectAccess,
    isFavourite,
  } = useProjectSelector(projects);

  const selectedProject = projects.find((p) => String(p.id) === String(selectedProjectId));

  // Calculate available height when popover opens
  React.useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Use the larger space available (popover will flip automatically)
      const maxSpace = Math.max(spaceBelow, spaceAbove);
      
      // Reserve space for: search (70px) + show more button (60px) + padding/margins (60px) + safety margin (30px)
      const reservedSpace = 220;
      // Ensure we don't exceed 80% of viewport height for safety
      const maxAllowedHeight = viewportHeight * 0.8;
      const calculatedHeight = Math.max(150, maxSpace - reservedSpace);
      const availableForLists = Math.min(calculatedHeight, maxAllowedHeight);
      
      setAvailableHeight(availableForLists);
    }
  }, [open]);

  const filteredProjects = React.useMemo(() => {
    let filtered = projects;

    // Filter by configured project statuses
    filtered = filtered.filter(project =>
      configuredProjectStatuses.includes(project.status)
    );

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          (project.billingCode || '').toLowerCase().includes(query) ||
          (project.name || '').toLowerCase().includes(query) ||
          (project.clientName || '').toLowerCase().includes(query) ||
          (project.projectManager || '').toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [projects, searchQuery, configuredProjectStatuses]);

  const sortedProjects = React.useMemo(() => {
    return [...filteredProjects].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [filteredProjects]);

  const handleSelect = (projectId: string) => {
    // If selecting the same project, just close the popover
    if (String(projectId) === String(selectedProjectId)) {
      setOpen(false);
      setSearchQuery('');
      return;
    }
    
    onSelect(projectId);
    recordProjectAccess(projectId);
    setOpen(false);
    setSearchQuery('');
  };

  const handleModalSelect = (projectId: string) => {
    // If selecting the same project, just close the modal
    if (String(projectId) === String(selectedProjectId)) {
      setModalOpen(false);
      return;
    }
    
    onSelect(projectId);
    recordProjectAccess(projectId);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect('');
  };

  const getProjectColor = (color?: string): string => {
    return color || '#3b82f6';
  };

  // Calculate how many items to show based on available height
  const { showFavourites, showRecents, favouritesHeight, recentsHeight } = React.useMemo(() => {
    const itemHeight = 44; // Approximate height per item
    const sectionHeaderHeight = 36; // Height of section header
    
    // Minimum space needed for favourites section (header + 1 item)
    const minFavouritesSpace = sectionHeaderHeight + itemHeight;
    // Space for 3 items in recently used
    const recentsReducedSpace = sectionHeaderHeight + (itemHeight * 3);
    
    const totalFavourites = favouriteProjects.length;
    const favouriteIds = new Set(favouriteProjects.map(p => String(p.id)));
    const totalRecents = recentProjects.filter(p => !favouriteIds.has(String(p.id))).length;
    
    // If we have enough space for everything, show all
    const idealFavouritesHeight = totalFavourites > 0 ? sectionHeaderHeight + (itemHeight * Math.min(totalFavourites, 5)) : 0;
    const idealRecentsHeight = totalRecents > 0 ? sectionHeaderHeight + (itemHeight * Math.min(totalRecents, 5)) : 0;
    
    if (availableHeight >= idealFavouritesHeight + idealRecentsHeight) {
      return {
        showFavourites: totalFavourites > 0,
        showRecents: totalRecents > 0,
        favouritesHeight: idealFavouritesHeight,
        recentsHeight: idealRecentsHeight,
      };
    }
    
    // If we can fit favourites + reduced recents, do that
    if (availableHeight >= idealFavouritesHeight + recentsReducedSpace) {
      return {
        showFavourites: totalFavourites > 0,
        showRecents: totalRecents > 0,
        favouritesHeight: idealFavouritesHeight,
        recentsHeight: recentsReducedSpace,
      };
    }
    
    // If we can only fit favourites, hide recents
    if (availableHeight >= minFavouritesSpace) {
      return {
        showFavourites: totalFavourites > 0,
        showRecents: false,
        favouritesHeight: Math.min(idealFavouritesHeight, availableHeight),
        recentsHeight: 0,
      };
    }
    
    // Not enough space for anything, hide both
    return {
      showFavourites: false,
      showRecents: false,
      favouritesHeight: 0,
      recentsHeight: 0,
    };
  }, [favouriteProjects, recentProjects, availableHeight]);

  // Filter favourites and recents by configured project statuses
  const filteredFavourites = React.useMemo(() => {
    return favouriteProjects.filter(p => configuredProjectStatuses.includes(p.status));
  }, [favouriteProjects, configuredProjectStatuses]);

  const filteredRecents = React.useMemo(() => {
    const favouriteIds = new Set(filteredFavourites.map(p => String(p.id)));
    return recentProjects
      .filter(p => configuredProjectStatuses.includes(p.status))
      .filter(p => !favouriteIds.has(String(p.id)));
  }, [recentProjects, filteredFavourites, configuredProjectStatuses]);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-start h-8 text-xs px-3',
              !selectedProject && 'text-muted-foreground',
              className
            )}
          >
            <div className="flex items-center gap-3 w-full">
              {selectedProject ? (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <span className="font-medium text-sm truncate block">
                      {selectedProject.name} ({selectedProject.billingCode})
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 shrink-0 hover:bg-destructive/10"
                    onClick={handleClear}
                    type="button"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors" />
                  </Button>
                </>
              ) : (
                <span className="flex-1 text-left">{placeholder}</span>
              )}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[500px] p-0" 
          align="start"
          sideOffset={5}
          collisionPadding={10}
        >
          <Command shouldFilter={false}>
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                placeholder="Search by code, name, client, or PM..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <CommandList 
              className="overflow-y-auto"
              style={{ maxHeight: `${availableHeight}px` }}
              onWheel={(e: React.WheelEvent) => {
                // Allow native scroll behavior
                e.stopPropagation();
              }}
            >
              {/* Show search results when user is searching */}
              {searchQuery && (
                <>
                  {sortedProjects.length === 0 ? (
                    <div className="py-6 text-center">
                      <Briefcase className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                      <p className="text-sm text-muted-foreground">No projects found</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Try adjusting your search
                      </p>
                    </div>
                  ) : (
                    <CommandGroup>
                      <div className="flex items-center justify-between px-3 py-2 text-xs text-muted-foreground">
                        <span>{sortedProjects.length} project{sortedProjects.length !== 1 ? 's' : ''} found</span>
                      </div>
                      {sortedProjects.map((project) => (
                        <CommandItem
                          key={String(project.id)}
                          value={String(project.id)}
                          onSelect={() => handleSelect(String(project.id))}
                          className="px-3 py-3 cursor-pointer"
                        >
                          <div className="flex items-center gap-3 w-full">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-sm truncate block">
                                {project.name} ({project.billingCode})
                              </span>
                              {project.clientName && (
                                <span className="text-xs text-muted-foreground truncate block">
                                  {project.clientName}
                                </span>
                              )}
                            </div>
                            <Check
                              className={cn(
                                'h-4 w-4 shrink-0',
                                String(selectedProjectId) === String(project.id) ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}

              {/* Show favourites and recents when not searching */}
              {!searchQuery && (
                <>
                  {showFavourites && filteredFavourites.length > 0 && (
                    <CommandGroup>
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Star className="h-3.5 w-3.5 text-yellow-500" />
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Favourites</span>
                      </div>
                      {filteredFavourites.map((project) => (
                          <CommandItem
                            key={`fav-${project.id}`}
                            value={`fav-${project.id}`}
                            onSelect={() => handleSelect(String(project.id))}
                            className="px-3 py-2 cursor-pointer"
                          >
                            <div className="flex items-center gap-3 w-full">
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-sm truncate block">
                                  {project.name} ({project.billingCode})
                                </span>
                                {project.clientName && (
                                  <span className="text-xs text-muted-foreground truncate block">
                                    {project.clientName}
                                  </span>
                                )}
                              </div>
                              <Check
                                className={cn(
                                  'h-4 w-4 shrink-0',
                                  String(selectedProjectId) === String(project.id) ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                            </div>
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  )}

                  {showRecents && filteredRecents.length > 0 && (
                    <CommandGroup>
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Recently Used</span>
                      </div>
                      {filteredRecents.map((project) => (
                          <CommandItem
                            key={`recent-${String(project.id)}`}
                            value={`recent-${String(project.id)}`}
                            onSelect={() => handleSelect(String(project.id))}
                            className="px-3 py-2 cursor-pointer"
                          >
                            <div className="flex items-center gap-3 w-full">
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-sm truncate block">
                                  {project.name} ({project.billingCode})
                                </span>
                                {project.clientName && (
                                  <span className="text-xs text-muted-foreground truncate block">
                                    {project.clientName}
                                  </span>
                                )}
                              </div>
                              <Check
                                className={cn(
                                  'h-4 w-4 shrink-0',
                                  String(selectedProjectId) === String(project.id) ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                            </div>
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  )}

                  {!showFavourites && !showRecents && (
                    <div className="py-6 text-center">
                      <Briefcase className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                      <p className="text-sm text-muted-foreground">No favourites or recent projects</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Click "Show More..." to browse all projects
                      </p>
                    </div>
                  )}
                </>
              )}
            </CommandList>

            <div className="border-t p-2">
              <Button
                variant="ghost"
                className="w-full justify-center text-sm"
                onClick={() => {
                  setOpen(false);
                  setModalOpen(true);
                }}
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                Show More...
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      <ProjectSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        projects={enrichedProjects}
        value={selectedProjectId}
        onSelect={handleModalSelect}
        onToggleFavourite={toggleFavourite}
        isFavourite={isFavourite}
        selectedProjectId={selectedProjectId}
        configuredStatuses={configuredProjectStatuses}
      />
    </>
  );
}
